import {
  Activity,
  ArrowRight,
  Gauge,
  Bell,
  Rocket,
  ShieldCheck,
  Cpu,
  Router,
  TrendingUp,
} from "lucide-react";
import dashboardImg from "@/assets/alcatraz/dashboard-dark.png";
import { MonitorMockup } from "@/components/MonitorMockup";
import { localizeUrl } from "@/lib/site";
import { useSite } from "@/hooks/use-site";

const features = [
  { icon: Activity, labelKey: "operationsFeatureTelemetry" },
  { icon: Rocket, labelKey: "operationsFeatureOta" },
  { icon: Router, labelKey: "operationsFeatureTr069" },
  { icon: Bell, labelKey: "operationsFeaturePush" },
  { icon: Gauge, labelKey: "operationsFeatureIperf" },
  { icon: ShieldCheck, labelKey: "operationsFeatureAccess" },
  { icon: Cpu, labelKey: "operationsFeatureDiagnostics" },
] as const;

export function Operations() {
  const { messages, site, href } = useSite();
  const alcatrazHref = localizeUrl("/solutions/alcatraz-dms", site.locale, href);

  const metrics = [
    { value: messages.operationsMetric1Value, label: messages.operationsMetric1Label },
    { value: messages.operationsMetric3Value, label: messages.operationsMetric3Label },
  ];

  return (
    <section
      id="operations"
      className="relative overflow-hidden border-t border-border/60 section-tint py-24 sm:py-32"
    >
      <div className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute left-1/2 top-0 h-[480px] w-[820px] -translate-x-1/2 rounded-full bg-primary/10 blur-3xl" />
      </div>

      <div className="mx-auto max-w-7xl px-6">
        <div className="mx-auto max-w-2xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-card px-3 py-1 text-xs text-muted-foreground">
            <span className="h-1.5 w-1.5 rounded-full bg-primary animate-pulse" />
            {messages.operationsEyebrow}
          </div>
          <h2 className="mt-4 text-4xl font-semibold tracking-tight sm:text-5xl">
            {messages.operationsTitle}{" "}
            <span className="text-gradient">{messages.operationsTitleAccent}</span>
          </h2>
          <p className="mt-4 text-lg text-muted-foreground">{messages.operationsDescription}</p>
          <div className="mt-6 flex justify-center">
            <a
              href={alcatrazHref}
              className="group inline-flex h-11 items-center gap-2 rounded-full bg-primary px-5 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/20 transition-all hover:-translate-y-0.5 hover:bg-primary/90 hover:shadow-xl hover:shadow-primary/25 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              {messages.operationsLearnMore}
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </a>
          </div>
        </div>

        {/* Live dashboard: monitor frame + floating telemetry callouts */}
        <div className="mx-auto mt-16 max-w-4xl">
          <MonitorMockup image={dashboardImg} alt={messages.operationsImageAlt}>
            {/* Online devices — anchored top-left, overhanging the bezel */}
            <div
              style={{ animationDelay: "0s" }}
              className="animate-float absolute -left-3 top-8 z-10 hidden items-center gap-2.5 rounded-xl border border-border/60 bg-card/95 px-3.5 py-2 shadow-xl shadow-primary/10 backdrop-blur sm:flex"
            >
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
              </span>
              <div className="leading-tight">
                <div className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                  {messages.operationsLiveBadge}
                </div>
                <div className="text-sm font-semibold text-foreground">
                  {messages.operationsCalloutOnline}
                </div>
              </div>
            </div>

            {/* Active-this-month trend — anchored mid-right */}
            <div
              style={{ animationDelay: "1.4s" }}
              className="animate-float absolute -right-3 top-[56%] z-10 hidden items-center gap-2 rounded-xl border border-border/60 bg-card/95 px-3.5 py-2 shadow-xl shadow-primary/10 backdrop-blur sm:flex"
            >
              <TrendingUp className="h-4 w-4 text-emerald-500" />
              <span className="text-sm font-medium text-foreground">
                {messages.operationsCalloutActive}
              </span>
            </div>

            {/* New firmware builds awaiting test — anchored lower-left */}
            <div
              style={{ animationDelay: "2.8s" }}
              className="animate-float absolute -left-3 bottom-12 z-10 hidden items-center gap-2 rounded-xl border border-border/60 bg-card/95 px-3.5 py-2 shadow-xl shadow-primary/10 backdrop-blur sm:flex"
            >
              <Rocket className="h-4 w-4 text-primary" />
              <span className="text-sm font-medium text-foreground">
                {messages.operationsCalloutFirmware}
              </span>
            </div>
          </MonitorMockup>
        </div>

        {/* Proof metrics */}
        <div className="mx-auto mt-14 flex max-w-3xl flex-wrap items-center justify-center gap-x-12 gap-y-6">
          {metrics.map((m) => (
            <div key={m.label} className="text-center">
              <div className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
                {m.value}
              </div>
              <div className="mt-1 text-sm text-muted-foreground">{m.label}</div>
            </div>
          ))}
        </div>

        <ul className="mx-auto mt-12 grid max-w-5xl grid-cols-2 gap-3 sm:grid-cols-3">
          {features.map(({ icon: Icon, labelKey }) => (
            <li
              key={labelKey}
              className="flex items-center gap-3 rounded-xl border border-border/60 bg-card/60 px-4 py-3 text-sm text-foreground backdrop-blur transition-colors hover:border-primary/30"
            >
              <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <Icon className="h-4 w-4" />
              </span>
              {messages[labelKey]}
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
